/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
    Links as ReactRouterLinks,
    Scripts as ReactRouterScripts,
    type LinksProps,
    type ScriptsProps,
    type UIMatch,
    useMatches,
} from 'react-router';
import { dedupePreloadResources, type PreloadResource } from '@/lib/page-designer/preload';

const MODULE_FETCH_POLICY = { crossOrigin: 'anonymous' as const };

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isPreloadResource(value: unknown): value is PreloadResource {
    return isRecord(value) && (value.kind === 'module' || value.kind === 'style') && typeof value.href === 'string';
}

function toPreloadResources(value: unknown): PreloadResource[] {
    return Array.isArray(value) ? value.filter(isPreloadResource) : [];
}

function collectPreloadResourcesFromMatches(matches: UIMatch[]): PreloadResource[] {
    return dedupePreloadResources(
        matches.flatMap((match) => {
            if (!isRecord(match.data)) return [];
            const page = isRecord(match.data.page) ? match.data.page : undefined;
            return [...toPreloadResources(match.data.preloadResources), ...toPreloadResources(page?.preloadResources)];
        })
    );
}

function useRoutePreloadResources(): PreloadResource[] {
    return collectPreloadResourcesFromMatches(useMatches());
}

export function PreloadResources({ resources }: { resources: PreloadResource[] }) {
    return resources.map((resource) =>
        resource.kind === 'module' ? (
            <link
                key={`${resource.kind}:${resource.href}`}
                rel="modulepreload"
                href={resource.href}
                crossOrigin={MODULE_FETCH_POLICY.crossOrigin}
            />
        ) : (
            <link key={`${resource.kind}:${resource.href}`} rel="stylesheet" href={resource.href} />
        )
    );
}

export function Links(props: LinksProps) {
    const resources = useRoutePreloadResources();
    return (
        <>
            <ReactRouterLinks {...props} />
            <PreloadResources resources={resources} />
        </>
    );
}

export function Scripts(props: ScriptsProps) {
    const resources = useRoutePreloadResources();
    const hasModulePreloads = resources.some((resource) => resource.kind === 'module');
    return (
        <ReactRouterScripts
            {...props}
            crossOrigin={hasModulePreloads ? MODULE_FETCH_POLICY.crossOrigin : props.crossOrigin}
        />
    );
}
